/**
 * Who owns the collection experience -- the Constructor.io widget or Shopify.
 *
 * The A/B test ships one set of markup: the Liquid collection is the stand-in
 * (see `skeletons.js`) and the widget takes it over when it is active. Both
 * experiences therefore drive the same hooks -- `[data-filter-*]`,
 * `[data-pagination-*]`, the sort select -- so the Shopify modules
 * (`modules/Filters.js`, `modules/CollectionPagination.js`) have to stand down
 * for exactly as long as the widget is in charge, and take over again the
 * moment it falls back. Without that, a filter change fires both a Constructor
 * browse request and a Shopify section fetch, and a group toggle handled twice
 * cancels itself out.
 *
 * This module is imported by the main bundle, so it must stay a leaf: no
 * imports of its own, nothing that would pull the widget chunk in with it.
 */
export const CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR =
  '[data-constructor-widget-collection], [data-constructor-priority-collection]';

/**
 * Set by `render.js#renderFallback()` when the widget hands the collection
 * back to the Liquid markup, which is also when Shopify takes over again.
 */
export const CONSTRUCTOR_COLLECTION_FALLBACK_ATTRIBUTE =
  'data-constructor-collection-fallback';

/**
 * The widget root for an element inside a collection, or the one on the page.
 * A collection page renders a single collection section, so the document-wide
 * lookup is what the delegated Shopify handlers need when the event they are
 * answering does not point at the widget.
 */
export const getConstructorCollectionWidget = (element) => {
  const closest = element?.closest?.(CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR);

  if (closest) {
    return closest;
  }

  return typeof document !== 'undefined'
    ? document.querySelector(CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR)
    : null;
};

export const isConstructorCollectionActive = (element) => {
  const widget = getConstructorCollectionWidget(element);

  return (
    Boolean(widget) &&
    !widget.hasAttribute(CONSTRUCTOR_COLLECTION_FALLBACK_ATTRIBUTE)
  );
};

export default isConstructorCollectionActive;
